import { Field } from '@m/alchemy-ui/Field';
import { Grid } from '@m/alchemy-ui/Grid';
import { GridCol } from '@m/alchemy-ui/GridCol';
import { GridRow } from '@m/alchemy-ui/GridRow';
import { Input } from '@m/alchemy-ui/Input';
import { Radio } from '@m/alchemy-ui/Radio';
import { RadioGroup } from '@m/alchemy-ui/RadioGroup';
import { type SlateToolbarControl } from '@m/alchemy-ui/SlateRTE';
import { IconButton } from '@m/alchemy-ui/IconButton';
import { InformationIcon } from '@m/alchemy-ui/icons/InformationIcon';
import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { Tooltip as AlchemyTooltip } from '@m/alchemy-ui/Tooltip';
import { Container, MultipleUpload, SingleUpload } from '../../../common/components';
import { ChallengeArchived, FileUploadType } from '../../../common/enums';
import { useFieldError, useFile } from '../../../hooks';
import { type IChallenge } from '../../../common/interfaces';
import { TranslatableInput, TranslatableRTE } from '../components';
import {
  FileUploadInstructions,
  LabelWithTooltip,
  PublishSection,
  StyledField,
  UploadFieldGap
} from './UserChallenge.styled';

const richTextEditorConfig = {
  description: {
    buttons: ['bold', 'italic', 'unorderedList', 'undo', 'redo'] as SlateToolbarControl[]
  }
};

export const UserChallengeDetails = () => {
  const intl = useIntl();
  const { values: challenge, handleChange, handleBlur, setFieldValue } = useFormikContext<IChallenge>();
  const [fieldError] = useFieldError();
  const [, UploadExtensions] = useFile();

  const title = intl.formatMessage({ id: 'title', defaultMessage: 'Title' });
  const subtitle = intl.formatMessage({ id: 'subtitle', defaultMessage: 'Subtitle' });
  const shortDescLabel = intl.formatMessage({ id: 'shortDescription', defaultMessage: 'Short Description' });
  const challengeListingImageLabel = intl.formatMessage({
    id: 'challengeListingImage',
    defaultMessage: 'Challenge listing image'
  });
  const otherFilesOrMediaLabel = intl.formatMessage({
    id: 'otherFilesOrMedia',
    defaultMessage: 'Other files or media'
  });

  return (
    <div>
      <Grid>
        <GridRow>
          <GridCol span={{ small: 12, medium: 6 }}>
            <Container>
              <StyledField>
                <Field
                  label={title}
                  required
                  isFullWidth
                  status={fieldError('title')}
                  input={
                    <TranslatableInput type="challenge" field="title" translateId={challenge.id} fieldLabel={title}>
                      <Input name="title" value={challenge.title} onChange={handleChange} onBlur={handleBlur} />
                    </TranslatableInput>
                  }
                />
              </StyledField>
              <StyledField>
                <Field
                  label={subtitle}
                  isFullWidth
                  status={fieldError('subtitle')}
                  input={
                    <TranslatableInput
                      type="challenge"
                      field="subtitle"
                      translateId={challenge.id}
                      fieldLabel={subtitle}
                    >
                      <Input
                        name="subtitle"
                        value={challenge.subtitle || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </TranslatableInput>
                  }
                />
                <FileUploadInstructions>
                  {intl.formatMessage({
                    id: 'subtitleHint',
                    defaultMessage: 'This appears with the heading on the homepage slider'
                  })}
                </FileUploadInstructions>
              </StyledField>
              <StyledField>
                <Field
                  label={shortDescLabel}
                  isFullWidth
                  input={
                    <TranslatableRTE
                      labelId="shortDescription"
                      type="challenge"
                      field="description"
                      translateId={challenge.id}
                      config={richTextEditorConfig.description}
                      fieldLabel={shortDescLabel}
                    />
                  }
                />
                <FileUploadInstructions>
                  {intl.formatMessage(
                    {
                      id: 'shortDescriptionHint',
                      defaultMessage: 'This will appear on the {challengePageLink} and the {ideaFormLink}.'
                    },
                    {
                      challengePageLink: (
                        <a
                          href="https://docs.medallia.com/en/?resourceId=idea-challenge-page"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {intl.formatMessage({
                            id: 'challengePage',
                            defaultMessage: 'challenge page'
                          })}
                        </a>
                      ),
                      ideaFormLink: (
                        <a
                          href="https://docs.medallia.com/en/?resourceId=idea-idea-form"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {intl.formatMessage({
                            id: 'ideaSubmissionForm',
                            defaultMessage: 'idea submission form'
                          })}
                        </a>
                      )
                    }
                  )}
                </FileUploadInstructions>
              </StyledField>
            </Container>
          </GridCol>
          <GridCol span={{ small: 12, medium: 6 }}>
            <Container>
              <UploadFieldGap>
                <Field
                  label={
                    <LabelWithTooltip>
                      {challengeListingImageLabel}
                      <AlchemyTooltip
                        trigger={
                          <IconButton
                            icon={<InformationIcon aria-hidden="true" size={14} />}
                            data-testid="listing-image-tooltip"
                          />
                        }
                        placement="right"
                        contents={intl.formatMessage({
                          id: 'challengeListingImageTooltip',
                          defaultMessage: 'This image represents the challenge around your Medallia Ideas community.'
                        })}
                      />
                    </LabelWithTooltip>
                  }
                  isFullWidth
                  labelId="listing-image"
                  input={
                    <SingleUpload
                      buttonLabel={intl.formatMessage({ id: 'attachImage', defaultMessage: 'Attach image' })}
                      buttonAriaLabel={`${intl.formatMessage({ id: 'attach', defaultMessage: 'Attach' })} ${challengeListingImageLabel}`}
                      fileTypes={UploadExtensions.image}
                      type={FileUploadType.CHALLENGE_LISTING}
                      labelId="listing-image"
                      describedBy="listing-image-instructions"
                    >
                      <FileUploadInstructions id="listing-image-instructions">
                        <p>
                          {intl.formatMessage({
                            id: 'otherFilesWeAcceptDragDrop',
                            defaultMessage: 'We accept jpg, gif and png files'
                          })}
                        </p>
                        <p>
                          {intl.formatMessage({
                            id: 'otherFilesWeAcceptDimensions',
                            defaultMessage: ' For best results upload a 464px by 302px image'
                          })}
                        </p>
                      </FileUploadInstructions>
                    </SingleUpload>
                  }
                />
              </UploadFieldGap>
              <Field
                label={
                  <LabelWithTooltip>
                    {otherFilesOrMediaLabel}
                    <AlchemyTooltip
                      trigger={
                        <IconButton
                          icon={<InformationIcon aria-hidden="true" size={14} />}
                          data-testid="other-files-tooltip"
                        />
                      }
                      placement="right"
                      contents={intl.formatMessage({
                        id: 'otherFilesOrMediaTooltip',
                        defaultMessage:
                          'Use this space to add files that are related to the challenge. Users can access these files on the challenge page.'
                      })}
                    />
                  </LabelWithTooltip>
                }
                isFullWidth
                labelId="otherFilesOrMedia"
                input={
                  <MultipleUpload
                    buttonLabel={intl.formatMessage({ id: 'attachFiles', defaultMessage: 'Attach files' })}
                    buttonAriaLabel={`${intl.formatMessage({ id: 'attach', defaultMessage: 'Attach' })} ${otherFilesOrMediaLabel}`}
                    fileTypes={[...UploadExtensions.image, ...UploadExtensions.video, ...UploadExtensions.raw]}
                    type={FileUploadType.CHALLENGE_ATTACHMENT}
                    labelId="otherFilesOrMedia"
                    describedBy="other-files-instructions"
                  >
                    <FileUploadInstructions>
                      <p id="other-files-instructions">
                        {intl.formatMessage({
                          id: 'otherFilesWeAccept',
                          defaultMessage: 'We accept jpg, gif, png, office docs, txt, pdf, zip, mp4, mov files'
                        })}
                      </p>
                    </FileUploadInstructions>
                  </MultipleUpload>
                }
              />
              <PublishSection>
                <Field
                  label={intl.formatMessage({ id: 'publishStatus', defaultMessage: 'Publishing' })}
                  isFullWidth
                  input={
                    <RadioGroup
                      layout="row"
                      selection={String(challenge.archived)}
                      onChange={(value: string) => setFieldValue('archived', Number(value))}
                    >
                      {({ handleChange }: { handleChange: (item: string) => void }) => [
                        <Radio
                          key="published"
                          label={intl.formatMessage({
                            id: 'challenge-publish-now',
                            defaultMessage: 'Yes & publish now'
                          })}
                          name="archived"
                          checked={challenge.archived === ChallengeArchived.PUBLISHED}
                          onChange={() => handleChange(String(ChallengeArchived.PUBLISHED))}
                        />,
                        <Radio
                          key="draft"
                          label={intl.formatMessage({ id: 'draft', defaultMessage: 'No, save as draft' })}
                          name="archived"
                          checked={challenge.archived === ChallengeArchived.UNPUBLISHED}
                          onChange={() => handleChange(String(ChallengeArchived.UNPUBLISHED))}
                        />
                      ]}
                    </RadioGroup>
                  }
                />
              </PublishSection>
            </Container>
          </GridCol>
        </GridRow>
      </Grid>
    </div>
  );
};
